/**
 * Deep (full-text) search over the *rendered* pages, run entirely in the browser.
 *
 * The public index is built by the backend into Storage under `public/pagefind/`,
 * and mirrored onto Hosting at build time (scripts/sync-pagefind.mjs) so it is
 * served same-origin. It cannot be loaded from Storage directly: Pagefind resolves
 * its wasm and index chunks relative to pagefind.js, but a media URL nests the path
 * under `/o/<percent-encoded>?alt=media`, and the bucket sends no CORS headers.
 *
 * The internal index (`internal/pagefind/`) is still unreachable — mirroring it onto
 * Hosting would publish it, and Pagefind has no hook to authenticate its own chunk
 * fetches. It needs the backend to serve it from a staff-gated, path-based route.
 * Until then staff deep search falls back to `GET /search` (titles/tags), which is
 * role-filtered server-side and always fresh.
 */

import DOMPurify from 'dompurify'

const PUBLIC_INDEX = (import.meta.env.VITE_PAGEFIND_PUBLIC as string) ?? '/pagefind/pagefind.js'

export interface PagefindHit {
  url: string
  title: string
  /** HTML with <mark> around the matched terms. */
  excerpt: string
  tags: string[]
}

interface RawResult {
  data: () => Promise<{
    url: string
    excerpt: string
    meta: { title?: string }
    filters?: Record<string, string[]>
  }>
}

interface PagefindApi {
  options: (o: Record<string, unknown>) => Promise<void>
  search: (q: string) => Promise<{ results: RawResult[] }>
}

let loading: Promise<PagefindApi | null> | null = null

async function load(): Promise<PagefindApi | null> {
  // Vite must not try to resolve or bundle this — the file is produced by the
  // backend's publish step and only exists on Hosting at runtime.
  loading ??= import(/* @vite-ignore */ PUBLIC_INDEX)
    .then(async (mod: PagefindApi) => {
      await mod.options({ excerptLength: 24 })
      return mod
    })
    .catch(() => null)
  return loading
}

/** Returns `null` when the index is unavailable (e.g. not published yet, or dev). */
export async function deepSearch(query: string, limit = 8): Promise<PagefindHit[] | null> {
  const pf = await load()
  if (!pf || !query.trim()) return pf ? [] : null

  const { results } = await pf.search(query)
  const top = await Promise.all(results.slice(0, limit).map((r) => r.data()))

  return top.map((d) => ({
    url: d.url,
    title: d.meta.title ?? d.url,
    // Pagefind builds the excerpt from page text and wraps matches in <mark>.
    // It is our own content, but it reaches React via dangerouslySetInnerHTML.
    excerpt: DOMPurify.sanitize(d.excerpt, { ALLOWED_TAGS: ['mark'], ALLOWED_ATTR: [] }),
    tags: d.filters?.tag ?? [],
  }))
}
